import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CalendarClock,
  CalendarDays,
  Clock,
  Share2,
  Star,
  Sunrise,
  Users,
  type LucideIcon,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { ProviderReviewBanner } from "@/components/account/provider/provider-review-banner";
import { ServicesPausedBanner } from "@/components/billing/services-paused-banner";
import { PageHeader } from "@/components/layout/page-header";
import { ReferralNudgeCard } from "@/components/referral/referral-nudge-card";
import { buttonVariants } from "@/components/ui/button-variants";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/stat-card";
import { useAppointmentTypeLabel } from "@/lib/appointment-labels";
import { localDateKey } from "@/lib/date/local-date-key";

export const Route = createFileRoute("/_authenticated/account/provider/dashboard")({
  component: ProviderDashboardPage,
});

// ─── Data ───────────────────────────────────────────────────────────────────
//
// Everything on this page is real data from existing endpoints (P7) — no
// placeholder zeros. Revenue and monthly consultations stay hidden until a
// provider analytics endpoint exists.

interface DashboardAppointment {
  id: string;
  startsAt: string;
  endsAt: string;
  type: string;
  consultationName: string | null;
  status: string;
  profile: { firstName: string; lastName: string } | null;
}

interface AppointmentsResponse {
  appointments: DashboardAppointment[];
  counts: { pending?: number };
}

interface SettingsDataLite {
  providerId: string | null;
}

interface SidebarData {
  provider?: { ratingAvg: number | null; reviewCount: number } | null;
}

const APPOINTMENTS_PATH = "/api/account/provider/appointments";

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function ProviderDashboardPage() {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;

  // The appointment endpoints answer only for the user's OWN provider
  // profile; a team-seat holder (receptionist, assistant) gets no data
  // cards rather than a page of errors.
  const settingsQ = useQuery({
    queryKey: ["api", "/api/account/user/settings-data"],
    queryFn: () => apiFetch.get<SettingsDataLite>("/api/account/user/settings-data"),
  });
  const ownsProvider = !!settingsQ.data?.providerId;

  const sidebarQ = useQuery({
    queryKey: ["api", "/api/account/sidebar-data"],
    queryFn: () => apiFetch.get<SidebarData>("/api/account/sidebar-data"),
  });

  const nextQ = useQuery({
    queryKey: ["api", APPOINTMENTS_PATH, "dashboard-next"],
    queryFn: () => apiFetch.get<AppointmentsResponse>(`${APPOINTMENTS_PATH}?tab=upcoming&limit=1`),
    enabled: ownsProvider,
  });

  // Today + tomorrow in one call. The API builds its day bounds in UTC, so
  // ask from yesterday to tomorrow and bucket by the visitor's LOCAL day.
  const now = new Date();
  const todayKey = localDateKey(now);
  const tomorrowKey = localDateKey(addDays(now, 1));
  const fromKey = localDateKey(addDays(now, -1));
  const daysQ = useQuery({
    queryKey: ["api", APPOINTMENTS_PATH, "dashboard-days", todayKey],
    queryFn: () =>
      apiFetch.get<AppointmentsResponse>(
        `${APPOINTMENTS_PATH}?tab=upcoming&from=${fromKey}&to=${tomorrowKey}&limit=100`,
      ),
    enabled: ownsProvider,
  });
  // `upcoming` holds only pending + confirmed. The auto-complete job turns a
  // confirmed visit into `completed` 30 min after it ends, so today's earlier
  // patients come back only from `past` (same window): merge both, one row
  // per appointment, or "Patients today" shrinks as the day goes on.
  const pastQ = useQuery({
    queryKey: ["api", APPOINTMENTS_PATH, "dashboard-past", todayKey],
    queryFn: () =>
      apiFetch.get<AppointmentsResponse>(
        `${APPOINTMENTS_PATH}?tab=past&from=${fromKey}&to=${todayKey}&limit=100`,
      ),
    enabled: ownsProvider,
  });
  const dayPending = daysQ.isPending || pastQ.isPending;
  const dayError = daysQ.isError || pastQ.isError;

  const byId = new Map<string, DashboardAppointment>();
  for (const a of [...(pastQ.data?.appointments ?? []), ...(daysQ.data?.appointments ?? [])]) {
    byId.set(a.id, a);
  }
  const days = [...byId.values()].sort(
    (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
  );
  const nowMs = now.getTime();
  const todayItems = days.filter((a) => localDateKey(new Date(a.startsAt)) === todayKey);
  const tomorrowItems = days.filter((a) => localDateKey(new Date(a.startsAt)) === tomorrowKey);
  const pendingCount = daysQ.data?.counts.pending ?? nextQ.data?.counts.pending ?? 0;
  const rating = sidebarQ.data?.provider ?? null;

  const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-8">
      <PageHeader
        title={<Trans>Dashboard</Trans>}
        description={<Trans>Welcome! Here's an overview of your activity.</Trans>}
      />

      <ProviderReviewBanner />

      <ServicesPausedBanner
        entity={
          settingsQ.data?.providerId
            ? { entityType: "provider", entityId: settingsQ.data.providerId }
            : null
        }
      />

      {ownsProvider ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label={<Trans>Patients today</Trans>}
            value={todayItems.length}
            icon={Users}
            tone="primary"
            loading={dayPending}
          />
          <StatCard
            label={<Trans>Awaiting confirmation</Trans>}
            value={pendingCount}
            icon={Clock}
            tone="warning"
            loading={daysQ.isPending}
          />
          <StatCard
            label={<Trans>Average rating</Trans>}
            value={rating?.ratingAvg != null ? rating.ratingAvg : "—"}
            format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
            icon={Star}
            tone="accent"
            hint={rating && rating.reviewCount === 0 ? t`No reviews yet` : undefined}
            loading={sidebarQ.isPending}
          />
          <StatCard
            label={<Trans>Total reviews</Trans>}
            value={rating?.reviewCount ?? 0}
            icon={Star}
            tone="info"
            loading={sidebarQ.isPending}
          />
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        {ownsProvider ? (
          <section className="border-border bg-card rounded-xl border p-5 lg:col-span-2">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-foreground text-base font-semibold">
                <Trans>Next patient</Trans>
              </h2>
              <Clock className="text-muted-foreground size-5" aria-hidden />
            </div>
            <div className="mt-4">
              {nextQ.isPending ? (
                <Skeleton className="h-20 w-full rounded-lg" />
              ) : nextQ.isError ? (
                <LoadErrorState
                  title={<Trans>Could not load appointments.</Trans>}
                  onRetry={() => void nextQ.refetch()}
                  retrying={nextQ.isFetching}
                />
              ) : nextQ.data.appointments[0] ? (
                <NextPatient appointment={nextQ.data.appointments[0]} locale={locale} />
              ) : (
                <EmptyBlock
                  icon={Users}
                  title={<Trans>No upcoming appointment</Trans>}
                  body={<Trans>Upcoming bookings will appear here.</Trans>}
                  cta={
                    <Link
                      to="/account/provider/booking-widget"
                      className={cn(buttonVariants({ size: "touch" }), "mt-4")}
                    >
                      <Share2 className="size-4" aria-hidden />
                      <Trans>Share your booking link</Trans>
                    </Link>
                  }
                />
              )}
            </div>
          </section>
        ) : null}

        <section className="border-border bg-card rounded-xl border p-5">
          <h2 className="text-foreground text-base font-semibold">
            <Trans>Quick actions</Trans>
          </h2>
          <div className="mt-4 space-y-3">
            <QuickAction
              to="/account/provider/calendar"
              icon={CalendarDays}
              label={<Trans>Manage schedule</Trans>}
            />
            <QuickAction
              to="/account/provider/profile"
              icon={Users}
              label={<Trans>Edit public profile</Trans>}
            />
            <QuickAction
              to="/account/provider/reviews"
              icon={Star}
              label={<Trans>View reviews</Trans>}
            />
          </div>
        </section>
      </div>

      {ownsProvider ? (
        <section className="border-border bg-card rounded-xl border p-5">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-foreground text-base font-semibold">
              <Trans>Today's schedule</Trans>
            </h2>
            <Link
              to="/account/provider/calendar"
              className="text-primary hover:text-primary-hover inline-flex min-h-11 items-center text-sm font-medium"
            >
              <Trans>View full calendar</Trans>
            </Link>
          </div>
          <div className="mt-4">
            {dayPending ? (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : dayError ? (
              <LoadErrorState
                title={<Trans>Could not load appointments.</Trans>}
                onRetry={() => {
                  void daysQ.refetch();
                  void pastQ.refetch();
                }}
                retrying={daysQ.isFetching || pastQ.isFetching}
              />
            ) : todayItems.length > 0 ? (
              <ul className="divide-border divide-y">
                {todayItems.map((a) => (
                  <ScheduleRow
                    key={a.id}
                    appointment={a}
                    timeFmt={timeFmt}
                    // The appointments tab that actually lists this visit:
                    // `upcoming` there means "starts from now on".
                    tab={
                      a.status === "pending"
                        ? "pending"
                        : new Date(a.startsAt).getTime() >= nowMs
                          ? "upcoming"
                          : "past"
                    }
                  />
                ))}
              </ul>
            ) : (
              <EmptyBlock
                icon={CalendarDays}
                title={<Trans>No appointments today</Trans>}
                body={<Trans>Configure your weekly schedule in calendar settings.</Trans>}
                cta={
                  <Link
                    to="/account/provider/profile"
                    search={{ tab: "calendar" }}
                    className={cn(buttonVariants({ size: "touch" }), "mt-4")}
                  >
                    <CalendarClock className="size-4" aria-hidden />
                    <Trans>Set your appointment slots</Trans>
                  </Link>
                }
              />
            )}
          </div>
        </section>
      ) : null}

      {ownsProvider && !daysQ.isPending && !daysQ.isError ? (
        <section className="border-border bg-card rounded-xl border p-5">
          <h2 className="text-foreground flex items-center gap-2 text-base font-semibold">
            <Sunrise className="text-warning size-5" aria-hidden />
            <Trans>Tomorrow's briefing</Trans>
          </h2>
          {tomorrowItems.length > 0 ? (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="border-border rounded-lg border p-3 text-center">
                <p className="text-foreground text-2xl font-semibold tabular-nums">
                  {tomorrowItems.length}
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  <Trans>Total patients</Trans>
                </p>
              </div>
              <div className="border-border rounded-lg border p-3 text-center">
                <p className="text-foreground text-sm font-medium tabular-nums">
                  {timeFmt.format(new Date(tomorrowItems[0]!.startsAt))} –{" "}
                  {timeFmt.format(new Date(tomorrowItems[tomorrowItems.length - 1]!.startsAt))}
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  <Trans>First / last</Trans>
                </p>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground mt-3 text-sm">
              <Trans>No appointments tomorrow yet</Trans>
            </p>
          )}
        </section>
      ) : null}

      {/* Referral nudge sits below the doctor's own data (P7). */}
      <ReferralNudgeCard variant="compact" />

      <AccountSecurityBanner />
    </div>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

function patientName(a: DashboardAppointment, unknown: string): string {
  return a.profile ? `${a.profile.firstName} ${a.profile.lastName}`.trim() : unknown;
}

function NextPatient({
  appointment: a,
  locale,
}: {
  appointment: DashboardAppointment;
  locale: string;
}) {
  const { t } = useLingui();
  const typeLabel = useAppointmentTypeLabel();
  const starts = new Date(a.startsAt);
  const when = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(starts);

  return (
    <Link
      to="/account/provider/appointments"
      search={{ tab: a.status === "pending" ? "pending" : "upcoming" }}
      className="border-border hover:bg-muted flex min-h-11 items-center gap-3 rounded-lg border p-4 transition-colors"
    >
      <div className="bg-primary-soft text-primary flex size-10 shrink-0 items-center justify-center rounded-full">
        <CalendarClock className="size-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-sm font-semibold">
          {patientName(a, t`Unknown patient`)}
        </p>
        <p className="text-muted-foreground truncate text-xs">
          {when} · {a.consultationName || typeLabel(a.type)}
        </p>
      </div>
      <ArrowRight className="text-muted-foreground size-4 shrink-0" aria-hidden />
    </Link>
  );
}

function ScheduleRow({
  appointment: a,
  timeFmt,
  tab,
}: {
  appointment: DashboardAppointment;
  timeFmt: Intl.DateTimeFormat;
  tab: "pending" | "upcoming" | "past";
}) {
  const { t } = useLingui();
  const typeLabel = useAppointmentTypeLabel();
  return (
    <li>
      <Link
        to="/account/provider/appointments"
        search={{ tab }}
        className="hover:bg-muted -mx-2 flex min-h-11 items-center gap-3 rounded-lg px-2 py-2.5 transition-colors"
      >
        <span className="text-foreground w-14 shrink-0 text-sm font-semibold tabular-nums">
          {timeFmt.format(new Date(a.startsAt))}
        </span>
        <span className="min-w-0 flex-1">
          <span className="text-foreground block truncate text-sm">
            {patientName(a, t`Unknown patient`)}
          </span>
          <span className="text-muted-foreground block truncate text-xs">
            {a.consultationName || typeLabel(a.type)}
          </span>
        </span>
        {a.status === "pending" ? (
          <span className="bg-warning-soft text-warning-strong shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold">
            <Trans>Pending</Trans>
          </span>
        ) : null}
      </Link>
    </li>
  );
}

function EmptyBlock({
  icon: Icon,
  title,
  body,
  cta,
}: {
  icon: LucideIcon;
  title: React.ReactNode;
  body: React.ReactNode;
  cta?: React.ReactNode;
}) {
  return (
    <div className="border-border flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-10 text-center">
      <Icon className="text-muted-foreground/60 size-10" aria-hidden />
      <p className="text-foreground mt-3 text-sm font-medium">{title}</p>
      <p className="text-muted-foreground mt-1 text-xs">{body}</p>
      {cta}
    </div>
  );
}

function QuickAction({
  to,
  icon: Icon,
  label,
}: {
  to: "/account/provider/calendar" | "/account/provider/profile" | "/account/provider/reviews";
  icon: LucideIcon;
  label: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="border-border hover:bg-muted flex min-h-11 items-center justify-between rounded-lg border px-4 py-3 transition-colors"
    >
      <span className="flex items-center gap-3">
        <Icon className="text-primary size-4" aria-hidden />
        <span className="text-foreground text-sm font-medium">{label}</span>
      </span>
      <ArrowRight className="text-muted-foreground size-4" aria-hidden />
    </Link>
  );
}
